'use client';

import { Box, BoxProps, Modal as MUIModal, ModalProps as MUIModalProps } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

export interface ModalHandle {
  show: () => void;
  hide: () => void;
}

export interface ModalProps extends Omit<MUIModalProps, 'open'> {
  bgColor?: string;
  width?: BoxProps['width'];
  padding?: BoxProps['padding'];
  borderRadius?: BoxProps['borderRadius'];
  boxProps?: Omit<BoxProps, 'padding' | 'width' | 'borderRadius' | 'bgcolor'>;
  handleClose?: () => void;
}

export const Modal = forwardRef<ModalHandle, ModalProps>(
  (
    {
      sx,
      children,
      boxProps,
      width = '480px',
      padding = '32px 24px',
      bgColor = 'white',
      borderRadius = '8px',
      handleClose,
      ...rest
    },
    ref,
  ) => {
    const [open, setOpen] = useState<boolean>(false);
    const [scrollPosition, setScrollPosition] = useState<number>(0);

    const onShow = useCallback(() => {
      // If the body is already fixed (another modal recently open), compute scroll from body's top
      const isBodyFixed = document.body.style.position === 'fixed';
      const preservedScroll = isBodyFixed ? Math.abs(parseInt(document.body.style.top || '0', 10)) : window.scrollY;
      setScrollPosition(preservedScroll || 0);
      setOpen(true);
    }, []);

    const onHide = useCallback(() => {
      handleClose?.();
      setOpen(false);
    }, [handleClose]);

    useImperativeHandle(ref, () => ({ show: onShow, hide: onHide }), [onShow, onHide]);

    useEffect(() => {
      if (open) {
        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        const originalStyles = {
          overflow: document.body.style.overflow,
          position: document.body.style.position,
          top: document.body.style.top,
          width: document.body.style.width,
          paddingRight: document.body.style.paddingRight,
        };

        // Save current scroll position and prevent scrolling
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollPosition}px`;
        document.body.style.width = '100%';
        document.body.style.paddingRight = `${scrollBarWidth}px`;

        return () => {
          // Restore original styles
          document.body.style.overflow = originalStyles.overflow;
          document.body.style.position = originalStyles.position;
          document.body.style.top = originalStyles.top;
          document.body.style.width = originalStyles.width;
          document.body.style.paddingRight = originalStyles.paddingRight;

          // Restore scroll position
          window.scrollTo(0, scrollPosition || 0);
        };
      }
    }, [open, scrollPosition]);

    return (
      <MUIModal
        open={open}
        disableScrollLock
        {...rest}
        sx={{
          '& .MuiBackdrop-root': {
            position: 'fixed',
          },
        }}>
        <Box
          width={`min(${width}, 100%)`}
          bgcolor={bgColor}
          padding={padding}
          borderRadius={borderRadius}
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            boxSizing: 'border-box',
            ':focus-visible': {
              outline: 'none',
            },
            ...sx,
          }}
          {...boxProps}>
          {children}
        </Box>
      </MUIModal>
    );
  },
);

Modal.displayName = 'Modal';
